<template>
  <print v-if="isPrint" :board="board" :form="form" />
  <div v-else>
    <!-- 기본정보 -->
    <field-select-module
      v-model="form.stdInfo"
      :board="this.board"
      :is-print="isPrint"
      :field-required="true"
      :options="[
        { type: 'date', label: '점검일', required: true },
        { type: 'text', label: '점검장소' },
        { type: 'disaster', label: '재해형태' },
        { type: 'alert', label: '알림 대상자 선택' },
      ]"
    />

    <!-- 점검대상 -->
    <div class="mt-28px">
      <h5 class="mb-4"><i class="dot" />{{ $t('점검대상') }}</h5>
      <div class="grid lg:grid-cols-12 gap-x-6 gap-y-4 mt-4">
        <div class="col-span-2 flex items-center" v-for="checkbox in form.inspectList" v-bind:key="checkbox.id">
          <div v-if="isPrint || board.readonly" class="flex">
            <div v-if="checkbox.check" style="background: #200c63" class="w-5 h-5 rounded-sm text-white flex justify-center items-center mr-2">
              <vs-icon icon="check" size="x-small"></vs-icon>
            </div>
            <div v-else style="border: 1px solid #200c63" class="w-5 h-5 rounded-sm text-white flex justify-center items-center mr-2">
              <vs-icon icon="check" size="x-small"></vs-icon>
            </div>
            {{ $t(checkbox.title) }}
          </div>
          <label v-else class="flex">
            <vs-checkbox :readonly="board.readonly" :vs-value="checkbox.title" v-model="checkbox.check"> </vs-checkbox>
            {{ $t(checkbox.title) }}
          </label>
        </div>
      </div>
    </div>

    <TableInspectMerge
      v-for="(inspection, inspection_i) in form.inspectList"
      :key="`inspection_${inspection_i}`"
      v-show="inspection.check"
      :is-print="isPrint"
      :is-readonly="board.readonly"
      :title="inspection.title"
      :tableHeaderNote="[{ text: '비고', width: 300 }]"
      v-model="inspection.list"
      class="mt-28px"
    />

    <!-- 현장근로자 의견 청취 및 특이사항 -->
    <div class="mt-28px">
      <h5 class="mb-4"><i class="dot"></i>{{ $t('현장근로자 의견 청취 및 특이사항') }}</h5>

      <!-- PC 버전 -->
      <div class="hidden xl:block">
        <div class="writeGridContainer">
          <div>
            <div>{{ $t('현장근로자 의견 청취') }}</div>
            <p v-if="isPrint" class="print-input">{{ form.inputC_1.fileName }}</p>
            <file-uploader v-else :readonly="board.readonly" v-model="form.inputC_1" />
          </div>
          <div class="writeGridLineItem">
            <div>{{ $t('특이사항') }}</div>
            <span v-if="isPrint" class="print-textarea"> {{ form.inputC_2 }} </span>
            <ml-textarea v-else rows="5" v-model="form.inputC_2" :readonly="board.readonly" />
          </div>
        </div>
      </div>

      <!-- 모바일 버전 -->
      <div class="block xl:hidden mt-4">
        <div class="mobile-field-wrapper mb-4">
          <div class="mobile-field-label">{{ $t('현장근로자 의견 청취') }}</div>
          <div class="mobile-field-content">
            <p v-if="isPrint" class="print-input">{{ form.inputC_1.fileName }}</p>
            <file-uploader v-else :readonly="board.readonly" v-model="form.inputC_1" class="w-full" />
          </div>
        </div>
        <div class="mobile-field-wrapper">
          <div class="mobile-field-label">{{ $t('특이사항') }}</div>
          <div class="mobile-field-content">
            <span v-if="isPrint" class="print-textarea"> {{ form.inputC_2 }} </span>
            <ml-textarea v-else class="w-full" rows="5" v-model="form.inputC_2" :readonly="board.readonly" />
          </div>
        </div>
      </div>
    </div>

    <!-- 결재란 -->
    <div class="mt-8">
      <div>
        <h5><i class="dot"></i>{{ $t("결재란") }}</h5>
      </div>
      <name-with-signature-row label-name="작성자" :name.sync="form.sign1.name" :signature.sync="form.sign1.sign" />
      <name-with-signature-row label-name="검토자" :name.sync="form.sign2.name" :signature.sync="form.sign2.sign" />
      <name-with-signature-row label-name="회람자" :name.sync="form.sign3.name" :signature.sync="form.sign3.sign" />
      <name-with-signature-row label-name="승인자" :name.sync="form.sign4.name" :signature.sync="form.sign4.sign" />
    </div>

    <!--    사진대지-->
    <PhotoUploadGridModule :row-count="2" :grid-count="2" v-model="form.photoList" :isPrint="isPrint" :readonly="board.readonly" section-title="사진대지" placeholder="신축현장 점검" class="mt-28px" />

    <!-- 파일첨부 -->
    <file-upload-module v-model="form.fileList" :is-print="isPrint" :readonly="board.readonly" class="mt-28px"></file-upload-module>
  </div>
</template>

<script>
import FileUploadModule from "@/views/modules/FileUploadModule";
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import PhotoUploadGridModule from "@/views/modules/PhotoUploadGridModule";
import FileUploader from "@/components/FileUploader";
import TableInspectMerge from "@/components/TableInspectMerge";
import Print from "./Print.vue";
import NameWithSignatureRow from "@/components/NameWithSignatureRow.vue";
import { required } from "vuelidate/lib/validators";

export default {
  components: {
    NameWithSignatureRow,
    Print,
    TableInspectMerge,
    FileUploader,
    PhotoUploadGridModule,
    FieldSelectModule,
    FileUploadModule,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    value: Object,
  },
  validations: {
    form: {
      stdInfo: {
        field_name: { required },
        점검일: { required },
      },
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        const keys = Object.keys(this.form);
        for (const key of keys) {
          if (value[key]) {
            this.form[key] = value[key];
          }
        }
      },
    },
    form: {
      immediate: true,
      deep: true,
      handler(value) {
        value.title = this.board.writer.department;
        this.$emit("input", value);
      },
    },
  },
  data() {
    return {
      form: {
        stdInfo: {},
        inputC_1: {},
        inputC_2: "",

        typeTitleList: [],
        inspectList: [
          {
            check: false,
            title: "보호구",
            list: [
              {
                content: "1. 보호구 착용 및 관리상태(청력, 호흡용)가 적절 한가?",
                isChecked: "",
              },
              {
                content: "2. 기타 보호구 착용 및 관리상태가 적절한가?",
                isChecked: "",
              },
            ],
          },
          {
            check: false,
            title: "환기 및 배기장치",
            list: [
              {
                content: "1. 환기상태 및 환기설비 작동상태는 적절한가?",
                isChecked: "",
              },
              {
                content: "2. 국소 배기장치 상태 및 작동, 관리는 적절한가?",
                isChecked: "",
              },
              {
                content: "3. 밀폐공간 주기적인 점검 및 파악(산소농도, 자연환기, 환기 사용 점검 등)",
                isChecked: "",
              },
            ],
          },
          {
            check: false,
            title: "표지",
            list: [
              {
                content: "1. 안전보건표지는 적절히 부착되어 있는가?",
                isChecked: "",
              },
              {
                content: "2. 산업안전보건법령요지는 눈에 띄는 곳에 게시되 어 있는가?",
                isChecked: "",
              },
              {
                content: "3. 화학물질 취급시 MSDS 게시 및 부착의무, 작업 공정별 관리요령 게시가 이루어져 있는가?",
                isChecked: "",
              },
            ],
          },
          {
            check: false,
            title: "작업자세",
            list: [
              {
                content: "1. 작업자세/동선 및 중량물 취급자세 (중량물 취급시 2인 1조 작업/ 이동보조도구 활 용 등)",
                isChecked: "",
              },
            ],
          },
          {
            check: false,
            title: "작업장",
            list: [
              {
                content: "1. 작업장 내 정리정돈, 위생상태/ 소화기 유무, 관리 상태 (통로확보, 분리수거함 등)\n",
                isChecked: "",
              },
              {
                content: "2. 시설물 파손여부, 청소, 점검 등 관리상태",
                isChecked: "",
              },
            ],
          },
          {
            check: false,
            title: "근로자",
            list: [
              {
                content: "1. 근로자의 부상, 외상 등 조치할 수 있는 응급구급함이 갖추어져 있는가?",
                isChecked: "",
              },
              {
                content: "2. 근로자의 정기적인 건강진단을 실시하고 있는가?",
                isChecked: "",
              },
              {
                content: "3. 교대근무 여부",
                isChecked: "",
              },
              {
                content: "4. 근로자의 질병 예방 및 사후관리 방안이 활용되 고 있는가? (휴식시간, 휴게시설, 유연근무제 등)",
                isChecked: "",
              },
            ],
          },
          {
            check: false,
            title: "교육",
            list: [
              {
                content: "1. 매달 정기안전보건교육을 실시하고 있는가?",
                isChecked: "",
              },
              {
                content: "2. 신규 근로자의 경우 안전보건교육을 실시하고 있는가?",
                isChecked: "",
              },
              {
                content: "3. 근로자의 특별교육 해당일 경우 이를 실시하고 있는가?",
                isChecked: "",
              },
            ],
          },
          {
            check: false,
            title: "직업성 질환",
            list: [
              {
                content: "1. 하루 4시간 이상 집중적으로 자료입력 등을 위한 키보드/마우스 조작작업에 해당이 되는가?",
                isChecked: "",
              },
              {
                content: "2. 하루 총 2시간 이상 쪼그리고 앉거나 무릎을 굽힌 자세의 작업에 해당이 되는가?",
                isChecked: "",
              },
              {
                content: "3. 하루 총 2시간 이상 4.5kg이상의 물건을 한 손으 로 들거나 동일한 힘으로 쥐는 작업에 해당이 되는가?",
                isChecked: "",
              },
              {
                content: "4. 5kg이상 중량물 취급 시 중량물 취급안내표시가 부착되어있는가?",
                isChecked: "",
              },
              {
                content: "5. 근무시 기타 질환 유발요인들이 있는가?",
                isChecked: "",
              },
            ],
          },
        ],
        photoList: [],
        fileList: [],

        sign1: {
          name: "",
          sign: "",
        },
        sign2: {
          name: "",
          sign: "",
        },
        sign3: {
          name: "",
          sign: "",
        },
        sign4: {
          name: "",
          sign: "",
        },
      },
    };
  },
  methods: {
    translationMap() {
      const prefix = "보건점검 현황/보건 순회점검표(현장)";

      const inspectList = this.form.inspectList.filter((item) => item.check);
      let _json = {};
      inspectList.map((item) => {
        const json = this.transformPath(item, prefix)
        _json = {
          ..._json,
          ...json
        }
      })

      const translateData = {
        [`${prefix}.기본정보.회사명`]: this.form.stdInfo.field_name,
        [`${prefix}.기본정보.작성자이름`]: this.form.stdInfo.writer_name,
        [`${prefix}.기본정보.작성일`]: this.form.stdInfo.date,
        [`${prefix}.기본정보.점검일`]: this.form.stdInfo.점검일,
        [`${prefix}.기본정보.점검장소`]: this.form.stdInfo.점검장소,
        [`${prefix}.기본정보.재해형태`]: this.form.stdInfo.재해형태,
        ..._json,
        [`${prefix}.현장근로자 의견 청취 및 특이사항.특이사항`]: this.form.inputC_2
      };

      return translateData;
    },
    translate(translate) {
      const prefix = "보건점검 현황/보건 순회점검표(현장)";

      this.form.stdInfo.field_name = translate[`${prefix}.기본정보.회사명`];
      this.form.stdInfo.writer_name = translate[`${prefix}.기본정보.작성자이름`];
      this.form.stdInfo.date = translate[`${prefix}.기본정보.작성일`];
      this.form.stdInfo.점검일 = translate[`${prefix}.기본정보.점검일`];
      this.form.stdInfo.점검장소 = translate[`${prefix}.기본정보.점검장소`];
      this.form.stdInfo.재해형태 = translate[`${prefix}.기본정보.재해형태`];
      this.form.inputC_2 = translate[`${prefix}.현장근로자 의견 청취 및 특이사항.특이사항`];

      const json = this.unflatten(translate, prefix);
      const inspectTitleList = this.form.inspectList.filter((item) => item.check).map(item => item.title)
      const _inspectList = JSON.parse(JSON.stringify(this.form.inspectList));
      for (const title of inspectTitleList) {
        const item = _inspectList.find(item => item.title === title)
        if (item.list) {
          for (let i = 0; i < item.list.length; i++) {
            item.list[i].content = json[title].list[i].점검내용;
            item.list[i].isChecked = json[title].list[i].상태;
            item.list[i].비고 = json[title].list[i].비고;
          }
        }
      }

      this.$set(this.form, "inspectList", _inspectList);
    },
    transformPath(data, globalPrefix = '' ) {
      const result = {};
      const title = data.title || "";

      data.list.forEach((item, idx) => {
        const prefix = `${title}.list_${idx}`;

        Object.entries(item).forEach(([key, value]) => {
          let newKey;
          if (key === "content") {
            newKey = "점검내용";
          } else if (key === "isChecked") {
            newKey = "상태";
          } else {
            newKey = key;
          }
          result[`${globalPrefix}.${prefix}.${newKey}`] = value;
        });
      });

      return result;
    },

    unflatten(flatObj, globalPrefix = "") {
      const nested = {};
      const prefixLength = globalPrefix ? globalPrefix.length + 1 : 0;

      for (const [flatKey, value] of Object.entries(flatObj)) {
        const keyPath = flatKey.slice(prefixLength).split(".");
        let current = nested;

        keyPath.forEach((part, idx) => {
          const isLast = idx === keyPath.length - 1;

          if (part.startsWith("list_")) {
            // list_x 처리
            const index = parseInt(part.split("_")[1], 10);
            if (!current.list) {
              current.list = [];
            }
            if (!current.list[index]) {
              current.list[index] = {};
            }
            if (isLast) {
              // 마지막 키면 값 대입
              // 배열 요소 객체에 필드 추가
              Object.assign(current.list[index], value);
            } else {
              current = current.list[index];
            }
          } else {
            // 일반 키 처리
            if (isLast) {
              current[part] = value;
            } else {
              if (!current[part]) {
                current[part] = {};
              }
              current = current[part];
            }
          }
        });
      }

      return nested;
    },

    validate() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        if (!this.$v.form.stdInfo.field_name.required) return "회사명을 반드시 입력해야 합니다";
        else if (!this.$v.form.stdInfo["점검일"].required) return "점검일을 반드시 입력해야 합니다";
      }
    },
  }
};
</script>

<style scoped lang="scss">
/* 모바일 스타일 */
.mobile-field-wrapper {
  margin-bottom: 20px;

  .mobile-field-label {
    font-weight: 500;
    font-size: 14px;
    margin-bottom: 10px;
  }

  .mobile-field-content {
    padding: 0;
  }
}
</style>
